// src/context/SessionClassContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react"
import { SessionData } from "../hooks/useSession"
import { ClassData } from "../hooks/useClasses"

// TYPES
interface SessionClassContextType {
  selectedSession: SessionData | null
  selectedClass: ClassData | null
  selectedClassName: string
  setSelectedSession: (session: SessionData | null) => void
  setSelectedClass: (classData: ClassData | null) => void
  clearAllStorage: () => void
}

const SessionClassContext = createContext<SessionClassContextType | undefined>(undefined)

// Local storage keys
const SESSION_STORAGE_KEY = 'selectedSession'
const CLASS_STORAGE_KEY = 'selectedClass'
const LAST_SCHOOL_ID_KEY = 'lastSchoolId'

// Helper functions for local storage
const saveToLocalStorage = (key: string, value: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
  }
}

const loadFromLocalStorage = (key: string) => {
  try {
    const item = localStorage.getItem(key)
    if (item) {
      const parsed = JSON.parse(item)
      // Basic validation - check if it has expected properties
      if (key === SESSION_STORAGE_KEY && parsed && typeof parsed === 'object' && parsed.id && parsed.name) {
        return parsed
      } else if (key === CLASS_STORAGE_KEY && parsed && typeof parsed === 'object' && parsed.id && parsed.name) {
        return parsed
      }
    }
    return null
  } catch (error) {
    // Clear invalid data
    try {
      localStorage.removeItem(key)
    } catch (clearError) {
    }
    return null
  }
}

const clearSessionClassStorage = () => {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY)
    localStorage.removeItem(CLASS_STORAGE_KEY)
  } catch (error) {
  }
}

const clearAllStorage = () => {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY)
    localStorage.removeItem(CLASS_STORAGE_KEY)
    localStorage.removeItem('selectedMedium')
    localStorage.removeItem('lastSchoolId')
    localStorage.removeItem(LAST_SCHOOL_ID_KEY)
  } catch (error) {
  }
}

export const SessionClassProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize state from localStorage
  const [selectedSession, setSelectedSession] = useState<SessionData | null>(() => 
    loadFromLocalStorage(SESSION_STORAGE_KEY)
  )
  const [selectedClass, setSelectedClass] = useState<ClassData | null>(() => 
    loadFromLocalStorage(CLASS_STORAGE_KEY)
  )
  const [selectedClassName, setSelectedClassName] = useState<string>(() => {
    const storedClass = loadFromLocalStorage(CLASS_STORAGE_KEY);
    return storedClass?.name || '';
  })

  // Save to localStorage whenever selectedSession changes
  useEffect(() => {
    if (selectedSession) {
      saveToLocalStorage(SESSION_STORAGE_KEY, selectedSession)
    } else {
      localStorage.removeItem(SESSION_STORAGE_KEY)
    }
  }, [selectedSession])

  // Save to localStorage whenever selectedClass changes and sync selectedClassName
  useEffect(() => {
    if (selectedClass) {
      saveToLocalStorage(CLASS_STORAGE_KEY, selectedClass)
      // Ensure selectedClassName is in sync with selectedClass
      if (selectedClassName !== selectedClass.name) {
        setSelectedClassName(selectedClass.name);
      }
    } else {
      localStorage.removeItem(CLASS_STORAGE_KEY)
      if (selectedClassName !== '') {
        setSelectedClassName('')
      }
    }
  }, [selectedClass, selectedClassName])

  // Check if school has changed and clear storage if needed
  useEffect(() => {
    const checkSchoolChange = () => {
      const lastSchoolId = localStorage.getItem(LAST_SCHOOL_ID_KEY)
      const currentSchoolId = localStorage.getItem('lastSchoolId') // This is set in Layout component
      
      if (lastSchoolId && currentSchoolId && lastSchoolId !== currentSchoolId) {
        // School has changed, clear session and class storage
        clearSessionClassStorage()
        localStorage.setItem(LAST_SCHOOL_ID_KEY, currentSchoolId)
      } else if (currentSchoolId && !lastSchoolId) {
        // First time setting school
        localStorage.setItem(LAST_SCHOOL_ID_KEY, currentSchoolId)
      }
    }

    // Check on mount and when storage changes
    checkSchoolChange()
    
    // Listen for storage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'lastSchoolId') {
        checkSchoolChange()
      }
    }
    
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const handleSetSelectedClass = (classData: ClassData | null) => {
    setSelectedClass(classData)
    setSelectedClassName(classData?.name || '')
  }

  // generate current session
  const getCurrentSession = (): string => {
    const currentYear = new Date().getFullYear()
    return `${currentYear}-${currentYear + 1}`
  }

  return (
    <SessionClassContext.Provider
      value={{ 
        selectedSession, 
        selectedClass, 
        selectedClassName,
        setSelectedSession, 
        setSelectedClass: handleSetSelectedClass,
        clearAllStorage
      }}
    >
      {children}
    </SessionClassContext.Provider>
  )
}

// useSessionClass() hook
export const useSessionClass = (): SessionClassContextType => {
  const context = useContext(SessionClassContext)
  if (!context) throw new Error("useSessionClass must be used within SessionClassProvider")
  return context
}
